import { useState, useCallback, useEffect, useRef } from "react";

const useScrollState = listRef => {
  const [scrollState, setScrollState] = useState({
    hasOverflow: false,
    isAtStart: true,
    isAtEnd: false,
    scrollLeft: 0,
    maxScrollLeft: 0
  });

  const scheduledRef = useRef(false);
  const timeoutIdRef = useRef(null);
  const rafIdRef = useRef(null);

  const updateScrollState = useCallback(() => {
    if (!listRef.current) return;
    const element = listRef.current;
    const scrollLeft = Math.round(element.scrollLeft);
    const maxScrollLeft = Math.round(element.scrollWidth - element.clientWidth);
    const hasOverflow = maxScrollLeft > 1;
    const tolerance = 1;
    const isAtStart = scrollLeft <= tolerance;
    const isAtEnd = scrollLeft >= maxScrollLeft - tolerance;
    setScrollState({
      hasOverflow,
      isAtStart,
      isAtEnd,
      scrollLeft,
      maxScrollLeft
    });
  }, [listRef]);

  const throttledScrollHandler = useCallback(() => {
    if (!scheduledRef.current) {
      scheduledRef.current = true;
      rafIdRef.current = requestAnimationFrame(() => {
        updateScrollState();
        scheduledRef.current = false;
      });
    }
  }, [updateScrollState]);

  const debouncedResizeHandler = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    timeoutIdRef.current = setTimeout(updateScrollState, 250);
  }, [updateScrollState]);

  useEffect(() => {
    const element = listRef.current;
    if (!element) return;

    // Initial state update
    updateScrollState();

    // Add event listeners
    element.addEventListener("scroll", throttledScrollHandler, {
      passive: true
    });
    window.addEventListener("resize", debouncedResizeHandler);

    return () => {
      element.removeEventListener("scroll", throttledScrollHandler);
      window.removeEventListener("resize", debouncedResizeHandler);
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [
    listRef.current, // Changed: now depends on the actual DOM element
    throttledScrollHandler,
    debouncedResizeHandler,
    updateScrollState
  ]);

  return {
    ...scrollState,
    updateScrollState
  };
};

export default useScrollState;
